import { useCallback, useRef, useState } from 'react'

import { CompletionPercentage } from '@harvey-frontend/openapi/models/CompletionPercentage'
import * as Sentry from '@sentry/react'
import { useMutation } from '@tanstack/react-query'

import {
  ReviewResponse,
  ReviewResponseSchema,
  ReviewStatus,
} from '../../../schemas/playbook'
import {
  StreamingResponseType,
  runPlaybookReview,
} from '../../../services/endpoints'
import { useAccessTokenContext } from '../providers/AccessTokenProvider'

type RunPlaybookReviewOptions = {
  onSuccess?: (data: ReviewResponse) => void
  onError?: (error: Error) => void
  onProgress?: (completion: CompletionStatus) => void
}

const RULE_SORT_ORDER: ReviewStatus[] = [
  'needs_review',
  'unacceptable',
  'acceptable',
]

export interface CompletionStatus {
  completion: CompletionPercentage | null
  rulesCompleted: number
}

export const useRunPlaybookReview = (
  playbook_json: string,
  wordDocumentId: string,
  options?: RunPlaybookReviewOptions
) => {
  const abortControllerRef = useRef<AbortController | null>(null)
  const { getAccessToken } = useAccessTokenContext()
  const [completion, setCompletion] = useState<CompletionStatus | null>(null)

  const mutation = useMutation({
    mutationFn: async (file: Blob) => {
      setCompletion(null)
      abortControllerRef.current = new AbortController()
      const formData = new FormData()
      const playbookBlob = new Blob([playbook_json], {
        type: 'application/json',
      })
      formData.append('playbook', playbookBlob, 'playbook.json')
      formData.append('file', file)
      const jsonBlob = new Blob(
        [JSON.stringify({ word_document_id: wordDocumentId })],
        {
          type: 'application/json',
        }
      )
      formData.append('word_document_id', jsonBlob)
      const token = await getAccessToken()
      if (!token) {
        throw new Error('No access token found')
      }
      const streamedResponses = await runPlaybookReview({
        accessToken: token,
        abortController: abortControllerRef.current,
        formData,
        updateCallback: async (partialResponse: StreamingResponseType) => {
          try {
            const parsedResponse = ReviewResponseSchema.parse(partialResponse)
            const completionStatus = {
              completion: parsedResponse.completion,
              rulesCompleted: parsedResponse.review.length,
            }
            setCompletion(completionStatus)
          } catch (error) {
            console.error('Error parsing partial response:', error)
            Sentry.captureException(error)
          }
        },
      })

      if (streamedResponses.length === 0) {
        throw new Error('No response received from the streaming endpoint')
      }

      const finalResponse = streamedResponses[streamedResponses.length - 1]

      try {
        const parsedResponse = ReviewResponseSchema.parse(finalResponse)
        return {
          ...parsedResponse,
          review: parsedResponse.review
            .sort(
              (a, b) =>
                RULE_SORT_ORDER.indexOf(a.status) -
                RULE_SORT_ORDER.indexOf(b.status)
            )
            .map((rule) => ({
              ...rule,
              resolved: false,
            })),
        }
      } catch (error) {
        console.error(error)
        throw error
      }
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  })

  const cancel = useCallback(() => {
    abortControllerRef.current?.abort()
    mutation.reset()
  }, [abortControllerRef, mutation])

  return {
    ...mutation,
    cancel,
    completion,
  }
}
